/*
 * Address arithmetic shared by the API and the web app: both validate
 * private networks, subnets, routes and firewall sources the same way.
 * IPv4 is handled as unsigned 32-bit numbers; IPv6 is only checked for syntax.
 */

/** An IPv4 network: first address and prefix length. */
export interface Ipv4Cidr {
  base: number;
  prefix: number;
}

const OCTET = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

/** The address as an unsigned number, or null when it is not a dotted IPv4 address. */
export function parseIpv4(value: string): number | null {
  const parts = value.split(".");
  if (parts.length !== 4 || !parts.every((p) => OCTET.test(p))) return null;
  return parts.reduce((sum, p) => sum * 256 + Number(p), 0);
}

export function formatIpv4(value: number): string {
  return [24, 16, 8, 0].map((shift) => Math.floor(value / 2 ** shift) % 256).join(".");
}

const size = (prefix: number) => 2 ** (32 - prefix);

/**
 * `a.b.c.d/n` whose address is the first of its block (`10.0.0.0/16`, not
 * `10.0.0.5/16`), as providers require for networks, subnets and routes.
 */
export function parseIpv4Cidr(value: string): Ipv4Cidr | null {
  const [address, length, ...rest] = value.trim().split("/");
  if (address === undefined || length === undefined || rest.length > 0) return null;
  if (!/^\d{1,2}$/.test(length)) return null;
  const prefix = Number(length);
  const base = parseIpv4(address);
  if (base === null || prefix > 32) return null;
  if (base % size(prefix) !== 0) return null;
  return { base, prefix };
}

export const formatIpv4Cidr = (cidr: Ipv4Cidr): string => `${formatIpv4(cidr.base)}/${cidr.prefix}`;

/** True when `inner` lies completely inside `outer` (equal counts as inside). */
export function cidrContains(outer: Ipv4Cidr, inner: Ipv4Cidr): boolean {
  return (
    inner.prefix >= outer.prefix &&
    inner.base >= outer.base &&
    inner.base + size(inner.prefix) <= outer.base + size(outer.prefix)
  );
}

export function cidrOverlaps(a: Ipv4Cidr, b: Ipv4Cidr): boolean {
  return a.base < b.base + size(b.prefix) && b.base < a.base + size(a.prefix);
}

export function ipInCidr(ip: number, cidr: Ipv4Cidr): boolean {
  return ip >= cidr.base && ip < cidr.base + size(cidr.prefix);
}

/** The three private IPv4 blocks of RFC 1918. */
export const PRIVATE_BLOCKS: readonly Ipv4Cidr[] = [
  { base: parseIpv4("10.0.0.0") ?? 0, prefix: 8 },
  { base: parseIpv4("172.16.0.0") ?? 0, prefix: 12 },
  { base: parseIpv4("192.168.0.0") ?? 0, prefix: 16 },
];

export const isPrivateCidr = (cidr: Ipv4Cidr): boolean =>
  PRIVATE_BLOCKS.some((block) => cidrContains(block, cidr));

/** The first block of `prefix` inside `range` (the default subnet of a new network). */
export function firstBlock(range: string, prefix = 24): string | null {
  const cidr = parseIpv4Cidr(range);
  if (!cidr || cidr.prefix > prefix) return null;
  return formatIpv4Cidr({ base: cidr.base, prefix });
}

/** Loose IPv6 check: hex groups, at most one `::`, at most eight groups. */
export function isIpv6(value: string): boolean {
  if (!/^[0-9a-fA-F:]+$/.test(value) || !value.includes(":")) return false;
  const doubles = value.split("::").length - 1;
  if (doubles > 1) return false;
  const groups = value.split(":").filter((g) => g !== "");
  if (groups.some((g) => g.length > 4)) return false;
  return doubles === 1 ? groups.length < 8 : groups.length === 8;
}

/**
 * An IPv4 or IPv6 address with a prefix, as firewall rules take them
 * (`0.0.0.0/0`, `203.0.113.7/32`, `2001:db8::/32`).
 */
export function isIpCidr(value: string): boolean {
  const [address, length, ...rest] = value.trim().split("/");
  if (address === undefined || length === undefined || rest.length > 0) return false;
  if (!/^\d{1,3}$/.test(length)) return false;
  const prefix = Number(length);
  if (parseIpv4(address) !== null) return prefix <= 32;
  return isIpv6(address) && prefix <= 128;
}
